// Cookie-consent platform constants + storage helpers.
//
// Consent is a device-local, tri-state decision that must survive the
// landing-page reset (`clearIfLandingPage` → `clearPersistedData` wipes the
// pipe's `pipe_store_*` localStorage key and all in-memory variables). So the
// tri-state decision lives in its OWN localStorage key — NOT under
// `pipe_store_*` — and a derived BOOLEAN ("is consent still pending?") is
// mirrored into the `global.isCookieConsentPending` runtime variable,
// re-asserted from here on every load (the `isRtl` pattern). The localStorage
// key is the source of truth; the variable is a reactive mirror that drives the
// banner's visibility rule.
//
// Why the variable is a boolean (not the tri-state): the visibility layer only
// needs the {undecided} vs {granted, denied} split (the banner hides on both
// Accept and Reject) — granted-vs-denied is only used by the Consent Mode
// signal + the stored record, which carry it independently. A boolean also
// gives admins a True/False rule picker instead of a magic string. It is framed
// POSITIVELY (true = pending = show) so a missing value (SSR / first paint)
// evaluates to hidden — no flash-of-banner for returning visitors.
//
// These names are FIXED platform constants (not author-configurable): the head
// consent-default script (GTM integration), the `set_cookie_consent` event
// handler, the `seedOriginDefaults` re-assert, and the author's visibility rule
// all reference the same keys, so they must agree. See the
// `global.isCookieConsentPending` seed in api `variable.seeder.ts`.

export type CookieConsentState = 'undecided' | 'granted' | 'denied'

/** localStorage key holding the persisted decision. Non-namespaced, by design. */
export const COOKIE_CONSENT_STORAGE_KEY = 'cookieConsent'

/**
 * Boolean runtime variable key (without the `var.` prefix) the banner's
 * visibility rule reads. `true` = consent still pending (undecided) → show the
 * banner. Rule: `var.global.isCookieConsentPending == true`.
 */
export const COOKIE_CONSENT_PENDING_VAR_KEY = 'global.isCookieConsentPending'

/** The boolean the variable mirror holds: true when no decision has been made. */
export function isConsentPending(state: CookieConsentState): boolean {
  return state === 'undecided'
}

/** Current persisted-record schema version (future-proofs the stored shape). */
export const COOKIE_CONSENT_RECORD_VERSION = 1

interface StoredConsentRecord {
  v: number
  state: 'granted' | 'denied'
  ts: number
}

/**
 * Read the persisted decision. Missing/garbage/unparseable → 'undecided' (the
 * safe default that surfaces the banner). SSR-safe and never throws.
 */
export function readStoredConsent(): CookieConsentState {
  if (typeof window === 'undefined') return 'undecided'
  try {
    const raw = window.localStorage?.getItem(COOKIE_CONSENT_STORAGE_KEY)
    if (!raw) return 'undecided'
    const parsed = JSON.parse(raw) as Partial<StoredConsentRecord>
    return parsed?.state === 'granted' || parsed?.state === 'denied' ? parsed.state : 'undecided'
  } catch {
    return 'undecided'
  }
}

/**
 * Persist a decision. 'undecided' clears the key (re-opens the banner on the
 * next load). SSR-safe and never throws.
 */
export function writeStoredConsent(state: CookieConsentState, now: number): void {
  if (typeof window === 'undefined') return
  try {
    if (state === 'undecided') {
      window.localStorage?.removeItem(COOKIE_CONSENT_STORAGE_KEY)
      return
    }
    const record: StoredConsentRecord = { v: COOKIE_CONSENT_RECORD_VERSION, state, ts: now }
    window.localStorage?.setItem(COOKIE_CONSENT_STORAGE_KEY, JSON.stringify(record))
  } catch {
    // Blocked/full localStorage degrades to "undecided" on next read; ignore.
  }
}
